import React, { useCallback, useRef, useEffect, useState } from "react";
import style from "../style/components/WYSIWYG.module.css";
import { BlockInput } from "../components/Inputs";

const toolbarButtons = [
    { name: "bold", label: "B" },
    { name: "italic", label: "I" },
    { name: "underline", label: "U" },
    { name: "strikethrough", label: "S" },
    { name: "title", label: "H1" },
    { name: "quote", label: "❝" },
    { name: "orderList", label: "OL" },
    { name: "unorderList", label: "UL" },
    { name: "link", label: "🔗" },
    { name: "img", label: "🖼" },
    { name: "video", label: "🎥" },
];

type EditorAsset = { tempUrl: string; file: File; type: "img" | "video" };

interface EditorProps {
    onChange?: (content: string) => void;
    onTitleChange?: (title: string) => void;
    onAssetsChange?: (assets: EditorAsset[]) => void;
    initialTitle?: string;
    initialContent?: string;
}

const Editor: React.FC<EditorProps> = ({
    onChange,
    onTitleChange,
    onAssetsChange,
    initialTitle = "",
    initialContent = "",
}) => {
    const [title, setTitle] = useState(initialTitle);
    const [titleError, setTitleError] = useState<string>("");
    const [assets, setAssets] = useState<EditorAsset[]>([]);
    const editorRef = useRef<HTMLDivElement | null>(null);
    const isComposing = useRef(false);
    const hasInitialized = useRef(false);
    const isFirstInput = useRef(true);

    useEffect(() => {
        setTitleError(title && title.length < 2 ? "제목은 2자 이상이어야 합니다." : "");
    }, [title]);

    useEffect(() => {
        if (editorRef.current && initialContent && !hasInitialized.current) {
            editorRef.current.innerHTML = initialContent;
            hasInitialized.current = true;
            isFirstInput.current = false;
        }
    }, [initialContent]);

    const handleInput = useCallback(() => {
        if (!editorRef.current || isComposing.current) return;

        const selection = window.getSelection();
        let savedRange: Range | null = null;

        if (!isFirstInput.current && selection && selection.rangeCount > 0) {
            savedRange = selection.getRangeAt(0).cloneRange();
        }

        const html = editorRef.current.innerHTML;

        onChange?.(html);

        if (isFirstInput.current) {
            isFirstInput.current = false;
            requestAnimationFrame(() => {
                if (editorRef.current) {
                    const range = document.createRange();
                    range.selectNodeContents(editorRef.current);
                    range.collapse(false);
                    const newSelection = window.getSelection();
                    if (newSelection) {
                        newSelection.removeAllRanges();
                        newSelection.addRange(range);
                    }
                }
            });
        } else if (savedRange) {
            requestAnimationFrame(() => {
                if (savedRange && editorRef.current) {
                    try {
                        const newSelection = window.getSelection();
                        if (newSelection) {
                            newSelection.removeAllRanges();
                            newSelection.addRange(savedRange);
                        }
                    } catch {
                        const range = document.createRange();
                        range.selectNodeContents(editorRef.current);
                        range.collapse(false);
                        const newSelection = window.getSelection();
                        if (newSelection) {
                            newSelection.removeAllRanges();
                            newSelection.addRange(range);
                        }
                    }
                }
            });
        }
    }, [onChange]);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;

        const handleCompStart = () => {
            isComposing.current = true;
        };
        const handleCompEnd = () => {
            isComposing.current = false;
            setTimeout(() => {
                handleInput();
            }, 0);
        };
        const handleFocus = () => {
            editor.dataset.placeholder = "";
            if (editor.innerHTML.trim() === "") {
                const p = document.createElement("p");
                p.appendChild(document.createElement("br"));
                editor.innerHTML = "";
                editor.appendChild(p);
                const range = document.createRange();
                const selection = window.getSelection();
                range.setStart(p, 0);
                range.collapse(true);
                if (selection) {
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }
        };
        const handleBlur = () => {
            if (editor.textContent?.trim() === "") editor.dataset.placeholder = "클릭하여 작성";
        };

        editor.addEventListener("focus", handleFocus);
        editor.addEventListener("blur", handleBlur);
        editor.addEventListener("compositionstart", handleCompStart);
        editor.addEventListener("compositionend", handleCompEnd);

        return () => {
            editor.removeEventListener("focus", handleFocus);
            editor.removeEventListener("blur", handleBlur);
            editor.removeEventListener("compositionstart", handleCompStart);
            editor.removeEventListener("compositionend", handleCompEnd);
        };
    }, [handleInput]);

    const onTitleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(e.target.value);
        onTitleChange?.(e.target.value);
    };

    const toggleBlock = (tag: string) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        let node = range.startContainer as Node | null;

        while (node && node.nodeType === Node.TEXT_NODE) node = node.parentNode!;
        while (node && node.parentNode && node.parentNode !== editorRef.current) node = node.parentNode;

        if (!node || !editorRef.current?.contains(node)) return;

        const block = node as HTMLElement;
        const currentTag = block.tagName?.toUpperCase();

        const startOffset = range.startOffset;
        const endOffset = range.endOffset;

        let newEl: HTMLElement;
        newEl = document.createElement(currentTag === tag.toUpperCase() ? "p" : tag.toLowerCase());
        newEl.innerHTML = block.innerHTML || "<br>";
        block.replaceWith(newEl);

        try {
            const newRange = document.createRange();
            const textNode = newEl.firstChild || newEl;
            newRange.setStart(textNode, Math.min(startOffset, textNode.textContent?.length || 0));
            newRange.setEnd(textNode, Math.min(endOffset, textNode.textContent?.length || 0));
            selection.removeAllRanges();
            selection.addRange(newRange);
        } catch {
            const newRange = document.createRange();
            newRange.selectNodeContents(newEl);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }

        setTimeout(() => {
            if (editorRef.current) onChange?.(editorRef.current.innerHTML);
        }, 0);
    };

    const insertMediaElement = (url: string, type: "img" | "video") => {
        const selection = document.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        const el = document.createElement(type);
        if (type === "video") {
            el.setAttribute("controls", "true");
            el.setAttribute("playsinline", "true");
            el.setAttribute("webkit-playsinline", "true");
            el.setAttribute("preload", "metadata");
            el.setAttribute("width", "100%");
            el.style.pointerEvents = "auto";
            el.style.maxWidth = "100%";
        }
        el.setAttribute("src", url);
        el.style.maxWidth = "100%";
        range.collapse(false);
        range.insertNode(el);
        const p = document.createElement("p");
        p.innerHTML = "<br>";
        el.after(p);
        range.setStart(p, 0);
        range.setEnd(p, 0);
        selection.removeAllRanges();
        selection.addRange(range);
        handleInput();
    };

    const handleCommand = async (cmd: string) => {
        editorRef.current?.focus();

        if (cmd === "title") {
            toggleBlock("H1");
        } else if (cmd === "quote") {
            toggleBlock("BLOCKQUOTE");
        } else if (cmd === "orderList") {
            document.execCommand("insertOrderedList");
        } else if (cmd === "unorderList") {
            document.execCommand("insertUnorderedList");
        } else if (cmd === "link") {
            const url = prompt("링크 주소 입력:");
            if (url) {
                const t = prompt("링크 텍스트:") || url;
                document.execCommand("insertHTML", false, `<a href='${url}' target='_blank'>${t}</a>`);
            }
        } else if (cmd === "img" || cmd === "video") {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = cmd === "img" ? "image/*" : "video/*";
            input.onchange = async () => {
                const file = input.files?.[0];
                if (!file) return;
                const tempUrl = URL.createObjectURL(file);
                insertMediaElement(tempUrl, cmd as "img" | "video");
                const next = [...assets, { tempUrl, file, type: cmd as "img" | "video" }];
                setAssets(next);
                onAssetsChange?.(next);
            };
            input.click();
        } else {
            document.execCommand(cmd);
        }
    };

    return (
        <div className={style["wrapper"]}>
            <div className={style["title-container"]}>
                <BlockInput
                    type="text"
                    placeholder="제목"
                    value={title}
                    setValue={(v) => {
                        setTitle(v);
                        onTitleChange?.(v);
                    }}
                    error={titleError}
                    onChange={onTitleInputChange}
                />
            </div>

            <div className={style["toolbar"]}>
                {toolbarButtons.map((btn) => (
                    <button
                        key={btn.name}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleCommand(btn.name);
                        }}
                        className={style["toolbar__button"]}
                    >
                        {btn.label}
                    </button>
                ))}
            </div>

            <div
                ref={editorRef}
                contentEditable
                className={style["editor"]}
                data-placeholder="클릭하여 작성"
                spellCheck={false}
                onInput={handleInput}
            />
        </div>
    );
};

export default Editor;
